// Shared formatters and color constants used by the MIS Analysis surface
// (`MisCategoryExpenses.jsx` and `MisExpenseStrips.jsx`). Pulling these into
// a separate module avoids the previous monolith of 1,800+ lines in
// MisCategoryExpenses.jsx that mixed UI, formatters, and constants.
//
// All helpers are pure / side-effect-free.

import { TrendingUp, TrendingDown, ArrowRight } from "lucide-react";

// ────── Number / currency / percent formatters ──────
export const fmtINR = (v) =>
  v == null || isNaN(v) ? "—" : "₹" + Math.round(v).toLocaleString("en-IN");

export const fmtL = (v) =>
  v == null || isNaN(v) ? "—" : "₹" + (Number(v) / 100000).toFixed(1) + "L";

export const fmtPct = (v) =>
  v == null || isNaN(v) ? "—" : `${Number(v).toFixed(1)}%`;

// ────── Variance / share / MoM tone ladders ──────
// All tone ladders use early-return if-chains (instead of nested ternaries) so they're
// easier to scan and modify. Each takes a number, returns a className string (or icon meta).

export const varianceTone = (pct) => {
  if (pct == null) return "text-zinc-500 bg-zinc-50 border-zinc-200";
  if (pct > 15)    return "text-rose-800 bg-rose-50 border-rose-200";
  if (pct > 5)     return "text-amber-800 bg-amber-50 border-amber-200";
  if (pct < -5)    return "text-emerald-800 bg-emerald-50 border-emerald-200";
  return "text-zinc-700 bg-zinc-50 border-zinc-200";
};

export const momTone = (pct) => {
  if (pct == null)  return { text: "text-zinc-500", bg: "bg-zinc-50", icon: ArrowRight };
  if (pct >= 25)    return { text: "text-rose-700", bg: "bg-rose-50", icon: TrendingUp };
  if (pct >= 10)    return { text: "text-amber-700", bg: "bg-amber-50", icon: TrendingUp };
  if (pct <= -10)   return { text: "text-emerald-700", bg: "bg-emerald-50", icon: TrendingDown };
  return { text: "text-zinc-600", bg: "bg-zinc-50", icon: ArrowRight };
};

export const shareTone = (pct) => {
  if (pct == null) return "bg-zinc-50 text-zinc-700";
  if (pct > 50)    return "bg-rose-50 text-rose-700";
  if (pct > 30)    return "bg-amber-50 text-amber-700";
  return "bg-zinc-50 text-zinc-700";
};

// YoY tone for the trend chip — expenses up = bad (rose), expenses down = good (emerald).
export const yoyChipTone = (pct) => {
  if (pct == null) return "text-zinc-500";
  if (pct > 15)    return "text-rose-700";
  if (pct > 5)     return "text-amber-700";
  if (pct < -5)    return "text-emerald-700";
  return "text-zinc-600";
};

// ────── Salary-dept ordering + color/abbr maps ──────
export const SALARY_DEPT_ORDER = [
  "Housekeeping", "Front Desk", "Maintenance", "Cluster Operation",
  "Salary Others", "Inter Branch Transfer", "IBT-Delhi Only", "Delhi Movement",
];

export const DEPT_COLOR = {
  "Housekeeping":          "#20B2AA",  // teal
  "Front Desk":            "#6366F1",  // indigo
  "Maintenance":           "#FB7185",  // rose
  "Cluster Operation":     "#FFA500",  // orange
  "Salary Others":         "#A78BFA",  // violet
  "Inter Branch Transfer": "#94a3b8",  // slate
  "IBT-Delhi Only":        "#cbd5e1",  // light slate
  "Delhi Movement":        "#fbbf24",  // amber
};

export const DEPT_ABBR = {
  "Housekeeping":          "HK",
  "Front Desk":            "FD",
  "Maintenance":           "MN",
  "Cluster Operation":     "CO",
  "Salary Others":         "OT",
  "Inter Branch Transfer": "IBT",
  "IBT-Delhi Only":        "IBT-D",
  "Delhi Movement":        "DM",
};

// ITC% tone (used by Finance Cost intel strip + cross-property table). Depends on the
// `benchmark_low_pct` / `benchmark_high_pct` thresholds in the FC summary payload.
// 4-step ladder: rose (>hi+3), emerald (lo..hi+3), amber (lo-5..lo), zinc otherwise.
export const itcPctTone = (pct, summary) => {
  if (pct == null) return "text-zinc-500 bg-zinc-50 border-zinc-200";
  const lo = summary?.benchmark_low_pct ?? 15;
  const hi = summary?.benchmark_high_pct ?? 18;
  if (pct > hi + 3)                  return "text-rose-800 bg-rose-50 border-rose-200";
  if (pct >= lo && pct <= hi + 3)    return "text-emerald-800 bg-emerald-50 border-emerald-200";
  if (pct >= lo - 5)                 return "text-amber-800 bg-amber-50 border-amber-200";
  return "text-zinc-700 bg-zinc-50 border-zinc-200";
};
